import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Mail, User, Shield, CheckCircle, Eye, EyeOff } from 'lucide-react';
import PublicNav from './components/PublicNav';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

export default function AuthScreen({ onLoginSuccess }) {
  // Registration now lives on /create-account for both gardeners and
  // designers. AuthScreen only handles sign-in and password reset.
  const [mode, setMode] = useState('login'); // 'login' | 'forgot-password'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    city: '',
    country: '',
    provinceState: '',
    agreeToTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [devResetUrl, setDevResetUrl] = useState(null);

  async function handleLogin(e) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.needsEmailVerification) {
          setError('Please verify your email address first. Check your inbox!');
        } else if (data.needsPasswordReset) {
          setError(data.error || 'It looks like your account still needs a password. Use "Forgot Password" to set one.');
        } else {
          setError(data.error || 'Login failed');
        }
        setLoading(false);
        return;
      }

      // Save token and user data - handle both customer and designer responses
      const userData = data.user || data.designer;
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userData', JSON.stringify(userData));
      // Also store as 'user' for compatibility with PlantADayApp
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Clear any errors and loading state
      setError(null);
      setLoading(false);
      
      // Call success callback (which will handle redirect based on role)
      onLoginSuccess(userData);
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again.');
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validation
    if (!formData.fullName || !formData.email || !formData.password || 
        !formData.phone || !formData.city || !formData.country || !formData.agreeToTerms) {
      setError('Please fill in all required fields and agree to terms');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          phone: formData.phone,
          city: formData.city,
          country: formData.country,
          provinceState: formData.provinceState,
          agreeToTerms: 'true',
          role: 'designer', // Designer account - has dashboard, can appear in Find a Designer
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Registration failed');
        setLoading(false);
        return;
      }

      setSuccessMessage(
        `Welcome to PlantADay! Your designer account is now active. After logging in, complete your profile to appear in "Find a Designer" and start managing your plant collections.`
      );
      
      // Switch to login mode after 5 seconds
      setTimeout(() => {
        setMode('login');
        setSuccessMessage(null);
        setFormData({ ...formData, password: '' });
      }, 8000);
      
      setLoading(false);
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
      setLoading(false);
    }
  }

  // Forgot password handler
  async function handleForgotPassword(e) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setDevResetUrl(null);
    setLoading(true);

    if (!formData.email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send reset email');
        setLoading(false);
        return;
      }

      // Dev fallback: server returns resetUrl directly when email isn't
      // configured yet. Show it in-place so we can still finish the flow.
      if (data.resetUrl) {
        setDevResetUrl(data.resetUrl);
        setSuccessMessage(
          `Email is not configured on the server, so we couldn't send a reset email to ${formData.email}. Use the reset link below to set a new password.`
        );
        setLoading(false);
        return;
      }

      setSuccessMessage(
        `Password reset email sent! Please check your inbox at ${formData.email} for instructions to reset your password. The link will expire in 1 hour.`
      );

      // Actually redirect back to login (the success screen says
      // "Redirecting to login..." but previously never did).
      setTimeout(() => {
        setMode('login');
        setSuccessMessage(null);
      }, 6000);

      setLoading(false);
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
      setLoading(false);
    }
  }

  const currentUserId = (() => {
    try {
      const raw = localStorage.getItem('userData') || localStorage.getItem('user');
      if (!raw) return null;
      return JSON.parse(raw)?.id || null;
    } catch {
      return null;
    }
  })();

  if (successMessage) {
    // Determine the title based on the message content
    const isPasswordReset = successMessage.includes('Password reset email');
    const isEmailNotConfigured = !!devResetUrl;
    const title = isEmailNotConfigured
      ? 'Use this reset link'
      : isPasswordReset
        ? 'Password Reset Email Sent!'
        : 'Registration Successful!';

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex flex-col">
        <PublicNav currentUserId={currentUserId} />
        <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">{title}</h2>
          <p className="text-gray-600 mb-4">{successMessage}</p>
          {devResetUrl ? (
            <div className="text-left">
              <a
                href={devResetUrl}
                className="inline-block w-full text-center py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition mb-3"
              >
                Open reset link
              </a>
              <p className="text-xs text-gray-500 break-all bg-gray-50 p-2 rounded">
                {devResetUrl}
              </p>
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setSuccessMessage(null);
                  setDevResetUrl(null);
                }}
                className="w-full mt-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Back to login
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Redirecting to login...</p>
          )}
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <div className="flex-1 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full flex flex-col md:flex-row">
        {/* Left Side - Branding. Gardener-first copy; designers sign in
            with the same form (role is determined by the account, not a
            tab), so no designer-specific surfacing is needed here. */}
        <div className="bg-gradient-to-br from-green-700 to-emerald-700 text-white p-6 sm:p-8 md:p-12 md:w-1/2 flex flex-col justify-center">
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <span className="text-3xl sm:text-4xl md:text-5xl" aria-hidden>🌿</span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                PlantaDay
              </h1>
            </div>
            <p className="text-green-100 text-base sm:text-lg font-semibold">Your garden, finally organised.</p>
            <p className="text-green-200 text-xs sm:text-sm mt-2">Sign in to open your garden, check this week&rsquo;s tasks, and log what you planted.</p>
          </div>

          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <span className="text-2xl mt-1" aria-hidden="true">📸</span>
              <div>
                <p className="font-semibold text-base">Your garden, zone by zone</p>
                <p className="text-sm text-green-100">Photo each bed, border, or patio and see how it changes across the seasons. Plant markers stay put so you always know what's planted where.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl mt-1" aria-hidden="true">✅</span>
              <div>
                <p className="font-semibold text-base">A simple list of what to do next</p>
                <p className="text-sm text-green-100">Tasks appear automatically from your plant care info: prune the roses this week, fertilise the lawn in May. No guesswork, no setup.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl mt-1" aria-hidden="true">✨</span>
              <div>
                <p className="font-semibold text-base">AI help when you need it</p>
                <p className="text-sm text-green-100">Ask about a wilted leaf, the best time to divide hostas, or what to plant in a shady corner. Your garden AI knows your plants.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Sign in only. Account creation lives on /create-account. */}
        <div className="p-6 sm:p-8 md:p-12 md:w-1/2">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {mode === 'forgot-password' ? 'Reset your password' : 'Welcome back'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {mode === 'forgot-password'
                ? 'We\u2019ll email you a link to set a new password.'
                : 'Sign in to pick up where you left off.'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base sm:text-sm min-h-[44px]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="text-right mt-1">
                    <button
                      type="button"
                      onClick={() => setMode('forgot-password')}
                      className="text-sm text-green-600 hover:text-green-800 font-medium"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
              
              {/* Prominent new-gardener CTA. /create-account accepts
                  accountType in navigation state so the gardener option
                  is preselected on arrival. */}
              <div className="mt-6 pt-5 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-700 mb-2">New to PlantaDay?</p>
                <Link
                  to="/create-account"
                  state={{ accountType: 'customer' }}
                  className="inline-flex items-center justify-center w-full py-3 rounded-lg font-medium border-2 border-green-600 text-green-700 hover:bg-green-50 transition text-sm sm:text-base min-h-[44px]"
                >
                  Start your garden {'\u2192'}
                </Link>
                <p className="text-xs text-gray-500 mt-3">
                  Are you a designer?{' '}
                  <Link
                    to="/create-account"
                    state={{ accountType: 'designer' }}
                    className="text-gray-600 hover:text-gray-800 underline"
                  >
                    Create a designer account
                  </Link>
                  .
                </p>
              </div>
            </>
          )}

          {/* Register Form (legacy — now disabled in favour of
              /create-account). Kept off behind a never-true check so the
              handleRegister helper and formData fields stay referenced
              elsewhere without dead-branch lint noise when we delete it
              in a follow-up cleanup. */}
          {false && (
            <form onSubmit={handleRegister} className="space-y-4 max-h-96 overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="Jane Smith"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password * (min 8 characters)</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="604-555-1234"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Vancouver"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Canada"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Province/State (Optional)</label>
                <input
                  type="text"
                  value={formData.provinceState}
                  onChange={(e) => setFormData({ ...formData, provinceState: e.target.value })}
                  placeholder="BC, CA, NY, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
                />
              </div>

              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                  className="mt-0.5"
                  required
                />
                <span className="text-gray-700 text-sm">
                  I agree to the{' '}
                  <Link to="/terms" className="text-green-600 hover:text-green-700 underline" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-green-600 hover:text-green-700 underline" target="_blank">
                    Privacy Policy
                  </Link>
                  . I confirm I am a landscape designer or plant professional.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                {loading ? 'Registering...' : 'Register as Designer'}
              </button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base sm:text-sm min-h-[44px]"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              {successMessage && (
                <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm">
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                  setSuccessMessage(null);
                }}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition"
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
